import { CAMPAIGN_TYPE, CAMPAIGN_TYPE_LABEL, ENTITY_KIND, GROUP_KIND } from '../../../constants/structure';
import { PLURAL, countLabel } from '../../../utils/plural';

/** provide/inject-ключ контекста дерева: раскрытие, поиск, доступы, действия. */
export const STRUCTURE_TREE_CONTEXT = Symbol('structure-tree-context');

const groupBy = (items, keyOf) => items.reduce((groups, item) => {
    const key = keyOf(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
    return groups;
}, new Map());

const toId = value => (value == null ? null : Number(value));

const emptyCampaignStats = () => ({
    total: 0,
    [CAMPAIGN_TYPE.GUARANTEED]: 0,
    [CAMPAIGN_TYPE.RTB]: 0,
    [CAMPAIGN_TYPE.RTB_PACKAGE]: 0,
});

const campaignStats = campaigns => campaigns.reduce((stats, campaign) => {
    stats.total += 1;
    stats[campaign.type] = (stats[campaign.type] || 0) + 1;
    return stats;
}, emptyCampaignStats());

export const nodeKey = (kind, id) => `${kind}-${id}`;

/**
 * Полное дерево из плоского data.structure.
 * Узел: { key, kind, entity, partner, title, children, campaigns, stats }; partner — партнёр ветки
 * (источник наценок её RTB-кампаний), null в служебных группах.
 * stats считаются по всему дереву (не по отфильтрованному) — от них зависят причины запрета удаления.
 *
 * @param {{ partners: Array, advertisers: Array, brands: Array, campaigns: Array }} structure
 * @returns {Array} корневые узлы: партнёры, затем служебные группы «Без партнёра» и «Без рекламодателя»
 */
export const buildStructureTree = ({ partners, advertisers, brands, campaigns }) => {
    const partnerIds = new Set(partners.map(partner => Number(partner.id)));
    const advertiserIds = new Set(advertisers.map(advertiser => Number(advertiser.id)));
    const brandIds = new Set(brands.map(brand => Number(brand.id)));

    const advertisersByPartner = groupBy(advertisers, advertiser => {
        const partnerId = toId(advertiser.partner_id);
        return partnerIds.has(partnerId) ? partnerId : null;
    });
    const brandsByAdvertiser = groupBy(brands, brand => toId(brand.advertiser_id));
    const campaignsByAdvertiser = groupBy(campaigns, campaign => {
        const advertiserId = toId(campaign.advertiser_id);
        return advertiserIds.has(advertiserId) ? advertiserId : null;
    });
    const campaignsByBrand = groupBy(
        campaigns.filter(campaign => brandIds.has(toId(campaign.brand_id))),
        campaign => toId(campaign.brand_id),
    );

    const brandNode = partner => (brand) => {
        const brandCampaigns = campaignsByBrand.get(Number(brand.id)) || [];
        return {
            key: nodeKey(ENTITY_KIND.BRAND, brand.id),
            kind: ENTITY_KIND.BRAND,
            entity: brand,
            partner,
            title: brand.name,
            children: [],
            campaigns: brandCampaigns,
            stats: { campaigns: campaignStats(brandCampaigns) },
        };
    };

    const advertiserNode = partner => (advertiser) => {
        const advertiserBrands = brandsByAdvertiser.get(Number(advertiser.id)) || [];
        const advertiserCampaigns = campaignsByAdvertiser.get(Number(advertiser.id)) || [];
        return {
            key: nodeKey(ENTITY_KIND.ADVERTISER, advertiser.id),
            kind: ENTITY_KIND.ADVERTISER,
            entity: advertiser,
            partner,
            title: advertiser.name,
            children: advertiserBrands.map(brandNode(partner)),
            campaigns: advertiserCampaigns.filter(campaign => !brandIds.has(toId(campaign.brand_id))),
            stats: { brands: advertiserBrands.length, campaigns: campaignStats(advertiserCampaigns) },
        };
    };

    const roots = partners.map((partner) => {
        const partnerAdvertisers = advertisersByPartner.get(Number(partner.id)) || [];
        return {
            key: nodeKey(ENTITY_KIND.PARTNER, partner.id),
            kind: ENTITY_KIND.PARTNER,
            entity: partner,
            partner,
            title: partner.name,
            children: partnerAdvertisers.map(advertiserNode(partner)),
            campaigns: [],
            stats: { advertisers: partnerAdvertisers.length },
        };
    });

    const withoutPartner = advertisersByPartner.get(null) || [];
    if (withoutPartner.length) {
        roots.push({
            key: GROUP_KIND.NO_PARTNER,
            kind: GROUP_KIND.NO_PARTNER,
            entity: null,
            partner: null,
            title: 'Без партнёра',
            children: withoutPartner.map(advertiserNode(null)),
            campaigns: [],
            stats: { advertisers: withoutPartner.length },
        });
    }

    const withoutAdvertiser = campaignsByAdvertiser.get(null) || [];
    if (withoutAdvertiser.length) {
        roots.push({
            key: GROUP_KIND.NO_ADVERTISER,
            kind: GROUP_KIND.NO_ADVERTISER,
            entity: null,
            partner: null,
            title: 'Кампании без рекламодателя',
            children: [],
            campaigns: withoutAdvertiser,
            stats: { campaigns: campaignStats(withoutAdvertiser) },
        });
    }

    return roots;
};

export const hasContent = node => node.children.length > 0 || node.campaigns.length > 0;

const includesQuery = (text, query) => String(text || '').toLowerCase().includes(query);

/**
 * Фильтр по партнёру и поиску по названию (партнёр, рекламодатель, бренд, кампания).
 * Совпавший узел остаётся со всем поддеревом; предки совпадений попадают в pathKeys —
 * их нужно раскрыть, иначе найденное не видно.
 *
 * @returns {{ roots: Array, pathKeys: Set<string> }}
 */
export const filterStructureTree = (roots, { query = '', partnerId = '' } = {}) => {
    const pathKeys = new Set();
    const normalizedQuery = query.trim().toLowerCase();

    const scoped = partnerId
        ? roots.filter(node => node.key === nodeKey(ENTITY_KIND.PARTNER, partnerId))
        : roots;

    if (!normalizedQuery) return { roots: scoped, pathKeys };

    const filterNode = (node) => {
        if (node.entity && includesQuery(node.title, normalizedQuery)) return node;

        const children = node.children.map(filterNode).filter(Boolean);
        const campaigns = node.campaigns.filter(campaign => includesQuery(campaign.name, normalizedQuery));
        if (!children.length && !campaigns.length) return null;

        pathKeys.add(node.key);
        return { ...node, children, campaigns };
    };

    return { roots: scoped.map(filterNode).filter(Boolean), pathKeys };
};

/** Ключи всех раскрываемых узлов — для «Развернуть всё». */
export const collectExpandableKeys = (roots) => {
    const keys = [];
    const walk = (node) => {
        if (!hasContent(node)) return;
        keys.push(node.key);
        node.children.forEach(walk);
    };
    roots.forEach(walk);
    return keys;
};

/** «2 гарант., 1 RTB» или «нет кампаний». */
export const campaignSummary = (stats) => {
    if (!stats?.total) return 'нет кампаний';
    const parts = Object.values(CAMPAIGN_TYPE)
        .filter(type => stats[type] > 0)
        .map(type => `${stats[type]} ${CAMPAIGN_TYPE_LABEL[type]}`);
    return `${countLabel(stats.total, PLURAL.CAMPAIGN)}: ${parts.join(', ')}`;
};

/**
 * Почему узел нельзя удалить — зеркало проверок PartnerService::delete и AdvertiserBrandHierarchyService.
 * Бэкенд остаётся источником правды; здесь — чтобы объяснить запрет до клика.
 *
 * @returns {string} пустая строка, если удаление разрешено
 */
export const deleteBlockReason = (node) => {
    const { stats, entity } = node;

    if (node.kind === ENTITY_KIND.PARTNER) {
        const reasons = [
            stats.advertisers > 0 && countLabel(stats.advertisers, PLURAL.ADVERTISER),
            entity.access_count > 0 && `${countLabel(entity.access_count, PLURAL.USER)} с доступом`,
        ].filter(Boolean);
        return reasons.length ? `Сначала уберите: ${reasons.join(', ')}` : '';
    }

    if (node.kind === ENTITY_KIND.ADVERTISER) {
        const reasons = [
            stats.brands > 0 && countLabel(stats.brands, PLURAL.BRAND),
            stats.campaigns.total > 0 && countLabel(stats.campaigns.total, PLURAL.CAMPAIGN),
        ].filter(Boolean);
        return reasons.length ? `Прикреплено: ${reasons.join(', ')}` : '';
    }

    if (node.kind === ENTITY_KIND.BRAND && stats.campaigns.total > 0) {
        return `Прикреплено: ${countLabel(stats.campaigns.total, PLURAL.CAMPAIGN)}`;
    }

    return '';
};

/**
 * Разбивает текст на фрагменты для подсветки совпадения: [{ text, isMatch }].
 */
export const splitByMatch = (text, query) => {
    const source = String(text || '');
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return [{ text: source, isMatch: false }];

    const parts = [];
    const lower = source.toLowerCase();
    let cursor = 0;
    let index = lower.indexOf(normalizedQuery);
    while (index !== -1) {
        if (index > cursor) parts.push({ text: source.slice(cursor, index), isMatch: false });
        parts.push({ text: source.slice(index, index + normalizedQuery.length), isMatch: true });
        cursor = index + normalizedQuery.length;
        index = lower.indexOf(normalizedQuery, cursor);
    }
    if (cursor < source.length) parts.push({ text: source.slice(cursor), isMatch: false });
    return parts;
};
